/**
 * MarketConditionCategoryModal Component
 *
 * Modal for creating/editing market condition categories.
 * Form fields: name (required), description (optional), color (required).
 * Color picker shows preset swatches with optional custom hex input.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Palette } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  TextArea,
  RadioGroup,
  Radio,
} from '@enfomentis/ui';
import { CATEGORY_COLORS } from '@/constants';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';

const CUSTOM_COLOR_KEY = '__custom__';
const HEX_REGEX = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Normalize hex color to include # prefix
 */
const normalizeHex = (hex: string): string => {
  const trimmed = hex.trim();
  if (trimmed.startsWith('#')) {
    return trimmed;
  }
  return `#${trimmed}`;
};

/**
 * Check if a hex color value is valid
 */
const isValidHex = (value: string): boolean => {
  return HEX_REGEX.test(value.trim());
};

export interface MarketConditionCategoryModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Existing category for edit mode (undefined for create) */
  category: MarketConditionCategory | undefined;
  /** Handler for saving - receives form data */
  onSave: (data: { name: string; description?: string; color: string }) => void;
  /** Whether save is in progress */
  isSaving?: boolean;
}

export const MarketConditionCategoryModal: React.FC<MarketConditionCategoryModalProps> = ({
  isOpen,
  onClose,
  category,
  onSave,
  isSaving = false,
}) => {
  const isEditMode = !!category;

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedColor, setSelectedColor] = useState(CATEGORY_COLORS[0]?.value || '#3b82f6');
  const [customHex, setCustomHex] = useState('');
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  // Reset form when modal opens/closes or category changes
  useEffect(() => {
    if (isOpen) {
      if (category) {
        // Edit mode - populate form
        setName(category.name);
        setDescription(category.description || '');

        // Check if color matches a preset
        const presetMatch = CATEGORY_COLORS.find(c => c.value === category.color);
        if (presetMatch) {
          setSelectedColor(presetMatch.value);
          setIsCustomSelected(false);
          setCustomHex('');
        } else {
          // Custom color
          setSelectedColor(CUSTOM_COLOR_KEY);
          setIsCustomSelected(true);
          setCustomHex(category.color);
        }
      } else {
        // Create mode - reset form
        setName('');
        setDescription('');
        setSelectedColor(CATEGORY_COLORS[0]?.value || '#3b82f6');
        setCustomHex('');
        setIsCustomSelected(false);
      }
    }
  }, [isOpen, category]);

  const handleColorChange = useCallback((value: string) => {
    if (value === CUSTOM_COLOR_KEY) {
      setIsCustomSelected(true);
      setSelectedColor(CUSTOM_COLOR_KEY);
    } else {
      setIsCustomSelected(false);
      setSelectedColor(value);
      setCustomHex('');
    }
  }, []);

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const data: { name: string; description?: string; color: string } = {
      name: trimmedName,
      color: isCustomSelected ? normalizeHex(customHex) : selectedColor,
    };

    const trimmedDescription = description.trim();
    if (trimmedDescription) {
      data.description = trimmedDescription;
    }

    onSave(data);
  };

  const isNameValid = name.trim().length >= 2;
  const isHexValid = !isCustomSelected || isValidHex(customHex);
  const canSave = isNameValid && isHexValid && !isSaving;

  // Get the current color for preview
  const previewColor = isCustomSelected
    ? (isValidHex(customHex) ? normalizeHex(customHex) : '#cccccc')
    : selectedColor;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      placement="top-center"
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex gap-2 items-center">
              <Palette className="w-5 h-5 text-primary" />
              <span>{isEditMode ? 'Edit Category' : 'Add Category'}</span>
            </ModalHeader>

            <ModalBody className="gap-4">
              <div className="flex flex-col gap-4">
                {/* Name */}
                <Input
                  label="Name"
                  placeholder="e.g., Volume, Market Structure, Trend Status"
                  value={name}
                  onValueChange={setName}
                  isRequired
                  variant="bordered"
                  classNames={{ input: 'text-base' }}
                  isInvalid={name.length > 0 && !isNameValid}
                  errorMessage={
                    name.length > 0 && !isNameValid
                      ? 'Name must be at least 2 characters'
                      : undefined
                  }
                  autoFocus
                />

                {/* Description */}
                <TextArea
                  label="Description"
                  placeholder="Optional: Describe what this category represents"
                  value={description}
                  onValueChange={setDescription}
                  variant="bordered"
                  classNames={{ input: 'text-base' }}
                  minRows={2}
                  maxRows={4}
                />

                {/* Color Picker */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-text-primary">
                    Color <span className="text-danger">*</span>
                  </label>

                  <RadioGroup
                    value={isCustomSelected ? CUSTOM_COLOR_KEY : selectedColor}
                    onValueChange={handleColorChange}
                    orientation="horizontal"
                    classNames={{ wrapper: 'gap-3 flex-wrap' }}
                  >
                    {CATEGORY_COLORS.map((color) => (
                      <Radio
                        key={color.value}
                        value={color.value}
                        classNames={{ base: 'm-0' }}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded-full border-2 border-default-300"
                            style={{ backgroundColor: color.value }}
                          />
                          <span className="text-sm">{color.name}</span>
                        </div>
                      </Radio>
                    ))}
                    <Radio
                      value={CUSTOM_COLOR_KEY}
                      classNames={{ base: 'm-0' }}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded-full border-2 border-default-300"
                          style={{
                            backgroundColor: isCustomSelected && isValidHex(customHex)
                              ? normalizeHex(customHex)
                              : '#cccccc',
                            backgroundImage: !isCustomSelected || !isValidHex(customHex)
                              ? 'linear-gradient(135deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%), linear-gradient(135deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%)'
                              : undefined,
                            backgroundSize: '8px 8px',
                            backgroundPosition: '0 0, 4px 4px',
                          }}
                        />
                        <span className="text-sm">Custom</span>
                      </div>
                    </Radio>
                  </RadioGroup>

                  {/* Custom Hex Input */}
                  {isCustomSelected && (
                    <div className="flex items-center gap-3 mt-2">
                      <Input
                        label="Hex Color"
                        placeholder="#ff5500"
                        value={customHex}
                        onValueChange={setCustomHex}
                        variant="bordered"
                        size="sm"
                        className="max-w-[160px]"
                        isInvalid={customHex.length > 0 && !isValidHex(customHex)}
                        errorMessage={
                          customHex.length > 0 && !isValidHex(customHex)
                            ? 'Invalid hex color'
                            : undefined
                        }
                      />
                      {isValidHex(customHex) && (
                        <div
                          className="w-8 h-8 rounded-full border-2 border-default-300 flex-shrink-0"
                          style={{ backgroundColor: normalizeHex(customHex) }}
                        />
                      )}
                    </div>
                  )}

                  {/* Color Preview */}
                  <div className="flex items-center gap-2 mt-2 p-2 bg-default-50 rounded-lg">
                    <span className="text-sm text-text-secondary">Preview:</span>
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: previewColor }}
                    />
                    <span className="text-sm font-medium">{name.trim() || 'Category Name'}</span>
                  </div>
                </div>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button variant="light" onPress={onClose} isDisabled={isSaving}>
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={handleSave}
                isDisabled={!canSave}
                isLoading={isSaving}
              >
                {isEditMode ? 'Save Changes' : 'Add Category'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
